import React from 'react'
import { ReactComponent as ZocksUpsellLogo } from '../../../../assets/ZocksUpsellLogo.svg'
import { DeltaTrialFeature } from '../DeltaTrialFeatures'
import { Button } from '../../../../components/Button/Button'
import { useNavigate } from 'react-router-dom'
import { useGetDeltaTrialsQuery, useGetPlansQuery } from '../../../../store/plan/plan'
import { useAppSelector } from '../../../../store/hooks'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { useZocksAuth } from '../../../../components/ScopedBlock/hooks'
import { DeltaTrialLoader } from '../Loader/DeltaTrialLoader'
import './Upsell.scss'

export interface UpsellProps {
  feature: DeltaTrialFeature
}

export const Upsell: React.FC<UpsellProps> = ({ feature }) => {
  const accountId = useAppSelector(selectAccountId)

  const navigate = useNavigate()
  const { hasScope } = useZocksAuth()

  const hasBillingCustomerReadPermission = hasScope('**', 'billing_customer:read', ['billing_customer:admin'])
  const hasBillingSubscriptionChangePermission = hasScope('**', 'billing_subscription:change', ['billing_subscription:admin'])

  const { data: plans } = useGetPlansQuery()
  const { data: deltaTrials } = useGetDeltaTrialsQuery({ accountId }, { skip: !hasBillingCustomerReadPermission })

  const { id: featureId, name: featureName, planId, calendly, UpsellBody, UpsellFeatures } = feature

  if (!plans?.data) {
    return (
      <div className='upsell-body d-flex align-items-center justify-content-center'>
        <DeltaTrialLoader message='Loading…' />
      </div>
    )
  }

  const deltaTrialPlan = plans.data.find((plan) => plan.id === planId)

  if (!deltaTrialPlan) {
    return <div>Plan not found for delta trial</div>
  }

  if (!deltaTrialPlan.deltaTrialDays) {
    return <div>Plan does not have delta trial</div>
  }

  const alreadyHadDeltaTrial = !!deltaTrials?.some((deltaTrial) => deltaTrial.planId === deltaTrialPlan.id)

  return (
    <div className='upsell-body'>
      <div className='d-flex align-items-center justify-content-center h-100'>
        <div className='d-flex flex-column flex-lg-row zocks-border-0.75 border border-1 zocks-border-color-gray-200 align-items-center upsell-container'>
          <div className='d-flex p-3 align-items-center w-100 upsell-inner-container'>
            <div className='zocks-border-0.5 upsell-left-container h-100 w-100 position-relative'>
              <ZocksUpsellLogo className='position-absolute z-0' />
              <div className='d-flex z-1 position-relative'>
                <div className='d-flex flex-column px-4 zocks-py-3.0 gap-4'>
                  <UpsellBody />
                </div>
              </div>
            </div>
          </div>
          <div className='d-flex p-3 align-items-center w-100 upsell-inner-container'>
            <div className='d-flex flex-column p-4 gap-4 h-100'>
              <div className='d-flex flex-column gap-4'>
                <div className='d-flex flex-column gap-2'>
                  <h4>
                    Try {deltaTrialPlan.name} for free to unlock {featureName}
                  </h4>
                  <div className='paragraph-medium'>
                    Start a free {deltaTrialPlan.deltaTrialDays}-day trial and unlock these top features in {featureName}:
                  </div>
                </div>
                <div className='d-flex flex-column gap-3'>
                  <UpsellFeatures />
                </div>
              </div>
              {!hasBillingSubscriptionChangePermission ? (
                <div className='d-flex flex-column p-2 align-items-center justify-content-center zocks-border-0.25 border border-1 zocks-border-color-gray-200 zocks-bg-color-gray-100'>
                  <div className='paragraph-medium bold'>Ready to get started? </div>
                  <div className='paragraph-medium'>Reach out to your Account Owner to request the trial.</div>
                </div>
              ) : alreadyHadDeltaTrial ? (
                <div className='d-flex flex-column p-2 align-items-center justify-content-center zocks-border-0.25 border border-1 zocks-border-color-gray-200 zocks-bg-color-gray-100'>
                  <div className='paragraph-medium bold'>Ready to get started? </div>
                  <span>
                    <Button
                      variant='link'
                      className='d-inline-flex button-medium text-decoration-none fw-normal zocks-text-primary'
                      onClick={() => navigate(`/account/plan/`)}
                    >
                      Upgrade
                    </Button>{' '}
                    to {deltaTrialPlan.name} today or{' '}
                    <a className='d-inline-flex zocks-text-primary' href={calendly} target='_blank' rel='noopener noreferrer'>
                      contact sales
                    </a>{' '}
                    for more details.
                  </span>
                </div>
              ) : (
                <div className='d-flex flex-column zocks-gap-0.75'>
                  <div className='d-flex gap-2'>
                    <Button variant='primary' className='button-medium flex-grow-1' onClick={() => navigate(`/account/plan/free-trial/${featureId}`)}>
                      Start free trial
                    </Button>
                    <a className='d-flex flex-grow-1' href={calendly} target='_blank' rel='noopener noreferrer'>
                      <Button variant='header' className='button-medium flex-grow-1'>
                        Book a demo
                      </Button>
                    </a>
                  </div>
                  <div className='paragraph-small text-center'>No strings attached, cancel anytime.</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
